// @flow
import {Unveil} from '@homeaway/react-unveil';
import gql from 'graphql-tag';
import {debounce} from 'lodash';
import React, {Component, Fragment} from 'react';
import Experiment from '@homeaway/react-experiment';
import HitPlaceholder from './HitPlaceholder';
import HitCarouselContainer from '../HitCarouselContainer/HitCarouselContainer';
import HitInfo, {fragments as hitInfoFragments} from '../HitInfo/HitInfo';
// $FlowFixMe
import TripBoardButton from '../TripBoardButton/TripBoardButton';
import {PHOTOCAROUSEL_ADVANCE} from '../../utils/carouselEdapUtils';
// $FlowFixMe
import {VirtualTourBadge} from '../VirtualTourBadge/VirtualTourBadge';
import {hitThumbnailBackgroundImageClass} from '../HitCollection/hitCollectionUtils';

import type {HitEventDataListingShape} from '../../utils/hitEdapUtils';
import type {Node} from 'react';
import type {ListingShape as HitInfoListingShape} from '../HitInfo/HitInfo';
import {HA_SERP_PREFETCH_HITS} from '../../abacus/experiments';
import {Link} from '@homeaway/react-localization-components';

import './Hit.less';
export const fragments = {
    listing: gql`
        fragment HitListing on Listing {
            virtualTourBadge {
                name
                id
                helpText
            }
            amenitiesBadges {
                name
                id
                helpText
            }
            multiUnitProperty
            imageCount
            images {
                altText
                c6_uri
                c9_uri
            }
            listingNamespace
            ...HitInfoListing
        }
        ${hitInfoFragments.listing}
    `
};

export type BadgeShape = {
    id: string,
    name: string,
    helpText: ?string
};

export type ListingImage = {
    altText: ?string,
    c6_uri: ?string,
    c9_uri: ?string
};

export type ListingShape = HitInfoListingShape & {
    availableForDates: ?boolean,
    virtualTourBadge: ?BadgeShape,
    multiUnitProperty: ?boolean,
    images: ?(ListingImage[]),
    imageCount: ?number,
    hitIndex?: number,
    page?: number,
    pageSize?: number
};

export type Props = {
    hitIndex: number,
    index: number,
    isGoodBot?: boolean,
    isWithoutAnchor?: boolean,
    listing?: ListingShape,
    onHitClick?: (HitEventDataListingShape) => void,
    onMouseEnter?: (listingId: string) => void,
    onMouseLeave?: (listingId: string) => void,
    page?: number,
    pageSize?: number
};

const HIT_UNVEIL_THRESHOLD = 600;
const HIT_UNVEIL_THROTTLE = 300;
const HIT_UNVEIL_MIN_HEIGHT = '232px';
const IMAGE_UNVEIL_THRESHOLD = 400;
const NON_UNVEILED_HIT_COUNT = 3;

const isIE11 = () => typeof window !== 'undefined' && window.__IE11__;

function HitImageUnveiler({children, index}: {index: number, children: Node}) {
    const classes = `HitCarousel Hit__thumbnail ${hitThumbnailBackgroundImageClass(
        index
    )}`;

    if (isIE11()) {
        return <div className={classes}>{children}</div>;
    }

    return (
        <Fragment>
            <Experiment name={HA_SERP_PREFETCH_HITS} variant={0}>
                <Unveil
                    className={classes}
                    threshold={IMAGE_UNVEIL_THRESHOLD}
                    throttleAmount={HIT_UNVEIL_THROTTLE}
                    useWindowAsScrollParent
                >
                    {children}
                </Unveil>
            </Experiment>

            <Experiment name={HA_SERP_PREFETCH_HITS} variant={1}>
                <div className={classes}>{children}</div>
            </Experiment>
        </Fragment>
    );
}

/**
 * This is used to wrap content with a "linky" (i.e. with href attribute) anchor tag on server-side render,
 * and loses its linkiness after component mounts.
 * Use case: Prior to unveil (i.e. prior to mount), the children content needs to be able to be clicked for navigation.
 * But the children content upon mount can have multiple click handlers (carousel swipes, etc) with which this anchor tag will compete.
 * By removing the linkiness on mount, other handlers registered on children content need not worry about this anchor's event behavior
 */
export class ServerSideAnchor extends Component<
    {
        children: Node,
        href: string,
        target?: '_blank',
        onClick: Function
    },
    {linky: boolean}
> {
    state = {linky: true};

    componentDidMount() {
        this.turnOffLink();
    }
    turnOffLink = () => {
        this.setState({linky: false});
    };

    render() {
        const {children, ...restProps} = this.props;
        const {linky} = this.state;

        const props = linky ? restProps : {};

        return <Link {...props}>{children}</Link>;
    }
}

function renderHit(props: Props) {
    const {
        onHitClick,
        onMouseEnter,
        onMouseLeave,
        index,
        listing,
        page,
        pageSize
    } = props;
    const handleMouseEnter = () =>
        listing && onMouseEnter ? onMouseEnter(listing.listingId) : null;
    const handleMouseLeave = () =>
        listing && onMouseLeave ? onMouseLeave(listing.listingId) : null;
    const onClick = () =>
        listing &&
        onHitClick &&
        onHitClick({listing, hitIndex: index, page, pageSize});
    const onMouseTimeout = 100;
    return (
        <div
            className="Hit media-flex media-flex--left media-flex--xs"
            data-wdio="hit"
            role="listitem"
            onMouseEnter={debounce(handleMouseEnter, onMouseTimeout)}
            onMouseLeave={debounce(handleMouseLeave, onMouseTimeout)}
        >
            {!listing ? (
                <HitPlaceholder />
            ) : (
                <Fragment>
                    <ServerSideAnchor
                        onClick={onClick}
                        target={'_blank'}
                        href={listing.detailPageUrl}
                    >
                        <HitImageUnveiler index={index}>
                            <span aria-hidden="true">
                                {listing.virtualTourBadge && (
                                    <VirtualTourBadge
                                        badge={listing.virtualTourBadge}
                                        detailPageUrl={listing.detailPageUrl}
                                        onBadgeClick={onClick}
                                    />
                                )}
                                <div className="Hit__tripboardButton">
                                    <TripBoardButton listing={listing} />
                                </div>
                                <HitCarouselContainer
                                    detailPageUrl={listing.detailPageUrl}
                                    edapAction={PHOTOCAROUSEL_ADVANCE}
                                    images={listing.images || []}
                                    listingId={listing.listingId}
                                    totalImageCount={listing.imageCount || 0}
                                    onImageClick={onClick}
                                />
                            </span>
                        </HitImageUnveiler>
                    </ServerSideAnchor>
                    <Link
                        target={'_blank'}
                        className="media-flex__content"
                        href={listing.detailPageUrl}
                        onClick={onClick}
                        tabIndex="0"
                    >
                        <HitInfo listing={listing} hitIndex={index} />
                    </Link>
                </Fragment>
            )}
        </div>
    );
}

export default function Hit(props: Props) {
    if (
        props.hitIndex >= NON_UNVEILED_HIT_COUNT &&
        !isIE11() &&
        !props.isGoodBot
    ) {
        return (
            <Fragment>
                <Experiment name={HA_SERP_PREFETCH_HITS} variant={0}>
                    {renderHit(props)}
                </Experiment>
                <Experiment name={HA_SERP_PREFETCH_HITS} variant={1}>
                    <Unveil
                        threshold={HIT_UNVEIL_THRESHOLD}
                        throttleAmount={HIT_UNVEIL_THROTTLE}
                        useWindowAsScrollParent
                        minContainerHeight={HIT_UNVEIL_MIN_HEIGHT}
                    >
                        {renderHit(props)}
                    </Unveil>
                </Experiment>
            </Fragment>
        );
    }
    return renderHit(props);
}
